import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';
import { normalizeIncomingSearchParams } from '../../util/searchParamMappers';
import { getSlugSearchParams, loadSlugEntries } from '../../util/categorySlugMap';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getFilterOptionCounts, resolveSearchFlags } from './SearchPage.shared';
import { sortListings } from './sortListings';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 6 columns (mobile: 2, medium: 3-4, large: 5, xlarge: 6)
// Using 30 as default (multiple of 2, 3, 5, 6) to ensure full rows on all screen sizes
// This will be adjusted dynamically on client side based on actual screen width
const RESULT_PAGE_SIZE = 30;
const MAX_API_PER_PAGE = 100;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FETCH_FILTER_COUNTS_REQUEST = 'app/SearchPage/FETCH_FILTER_COUNTS_REQUEST';
export const FETCH_FILTER_COUNTS_SUCCESS = 'app/SearchPage/FETCH_FILTER_COUNTS_SUCCESS';
export const FETCH_FILTER_COUNTS_ERROR = 'app/SearchPage/FETCH_FILTER_COUNTS_ERROR';
export const SET_PER_PAGE_OVERRIDE = 'app/SearchPage/SET_PER_PAGE_OVERRIDE';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  filterOptionCounts: {},
  filterOptionCountsInProgress: false,
  perPageOverride: null,
};

// Helper function to check if a listing has an active promotion
// A product is on promotion if its current price is strictly lower than original_price
const hasActivePromotion = listing => {
  const publicData = listing.attributes?.publicData || {};
  const originalPrice = publicData?.original_price;
  const currentPrice = listing.attributes?.price;

  // Must have both original_price and current price
  if (originalPrice == null || !currentPrice || !currentPrice.amount) {
    return false;
  }

  try {
    // original_price is stored in main units (e.g., 19.99)
    // currentPrice.amount is in subunits (e.g., 1999 cents)
    // We need to convert original_price to subunits for comparison
    const subUnitDivisor = unitDivisor(currentPrice.currency);
    const originalPriceInSubunits = convertUnitToSubUnit(originalPrice, subUnitDivisor);
    const currentPriceAmount =
      typeof currentPrice.amount === 'number'
        ? currentPrice.amount
        : parseFloat(currentPrice.amount);

    // Check if current price is strictly lower than original price
    return currentPriceAmount < originalPriceInSubunits;
  } catch (error) {
    // If conversion fails, don't consider it a promotion
    return false;
  }
};

const resultIds = (data, filterPromotions = false) => {
  const listings = data.data;
  return listings
    .filter(l => {
      // Basic filters: not deleted and published
      if (l.attributes.deleted || l.attributes.state !== 'published') {
        return false;
      }
      // If filterPromotions is true, only return listings with active promotions
      if (filterPromotions) {
        return hasActivePromotion(l);
      }
      return true;
    })
    .map(l => l.id);
};

// Helper to filter all listings for promotions and return the filtered list
const filterPromotionsFromListings = allListings => {
  return allListings.filter(l => {
    // Basic filters: not deleted and published
    if (l.attributes.deleted || l.attributes.state !== 'published') {
      return false;
    }
    // Only return listings with active promotions
    return hasActivePromotion(l);
  });
};

const shuffleArray = items => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const getSortingPriority = listing => {
  const rawPriority = listing?.attributes?.publicData?.sorting_priority;
  if (rawPriority == null) return null;
  const priority = typeof rawPriority === 'number' ? rawPriority : parseFloat(rawPriority);
  return Number.isFinite(priority) && priority !== 0 ? priority : null;
};

const applyPriorityOrdering = (listings, { randomizeRest = false } = {}) => {
  const prioritized = [];
  const rest = [];

  listings.forEach(listing => {
    const priority = getSortingPriority(listing);
    if (priority != null) {
      prioritized.push({ listing, priority });
    } else {
      rest.push(listing);
    }
  });

  if (prioritized.length === 0) {
    return { hasPriority: false, listings };
  }

  prioritized.sort((a, b) => b.priority - a.priority);
  const orderedRest = randomizeRest ? shuffleArray(rest) : rest;
  return {
    hasPriority: true,
    listings: [...prioritized.map(p => p.listing), ...orderedRest],
  };
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        currentPageResultIds: [],
        searchListingsError: null,
        filterOptionCounts: {},
        filterOptionCountsInProgress: false,
      };
    case SEARCH_LISTINGS_SUCCESS:
      const { data, isDefaultSort = false } = payload;
      // Promotions (and other extra filters) are applied before pagination in searchListings.
      let resultIdsToUse = resultIds(data, false);
      let listingsForOrdering = data.data;

      if (isDefaultSort) {
        const { hasPriority, listings: orderedListings } = applyPriorityOrdering(
          listingsForOrdering
        );
        if (hasPriority) {
          resultIdsToUse = orderedListings.map(l => l.id);
        }
      }

      return {
        ...state,
        currentPageResultIds: resultIdsToUse,
        pagination: data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    case FETCH_FILTER_COUNTS_REQUEST:
      return {
        ...state,
        filterOptionCountsInProgress: true,
        // Keep existing counts during loading to avoid showing zeros temporarily
      };
    case FETCH_FILTER_COUNTS_SUCCESS:
      // Merge new counts with existing ones to avoid showing zeros during partial updates
      const newCounts = payload.filterOptionCounts || {};
      const existingCounts = state.filterOptionCounts || {};
      const mergedCounts = { ...existingCounts };

      // Merge each filter's counts, keeping existing values if new ones are missing
      Object.keys(newCounts).forEach(filterKey => {
        mergedCounts[filterKey] = { ...existingCounts[filterKey], ...newCounts[filterKey] };
      });

      return {
        ...state,
        filterOptionCounts: mergedCounts,
        filterOptionCountsInProgress: false,
      };
    case FETCH_FILTER_COUNTS_ERROR:
      return {
        ...state,
        filterOptionCountsInProgress: false,
      };
    case SET_PER_PAGE_OVERRIDE:
      return {
        ...state,
        perPageOverride: payload?.perPage ?? null,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => {
  // console.log('🚀 SearchPage searchListingsRequest:', {
  //   component: 'SearchPage.duck',
  //   searchParams,
  //   timestamp: new Date().toISOString()
  // });

  return {
    type: SEARCH_LISTINGS_REQUEST,
    payload: { searchParams },
  };
};

export const searchListingsSuccess = (
  response,
  sort,
  filterPromotions = false,
  randomizeResults = false,
  isDefaultSort = false
) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data, sort, filterPromotions, randomizeResults, isDefaultSort },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchFilterCountsRequest = () => ({
  type: FETCH_FILTER_COUNTS_REQUEST,
});

export const fetchFilterCountsSuccess = filterOptionCounts => ({
  type: FETCH_FILTER_COUNTS_SUCCESS,
  payload: { filterOptionCounts },
});

export const fetchFilterCountsError = e => ({
  type: FETCH_FILTER_COUNTS_ERROR,
  error: true,
  payload: e,
});

export const setPerPageOverride = perPage => ({
  type: SET_PER_PAGE_OVERRIDE,
  payload: { perPage },
});

export const searchListings = (
  searchParams,
  config,
  filterPromotions = false,
  randomizeResults = false,
  isDefaultSort = false
) => (dispatch, getState, sdk) => {
  // console.log('🔍 SearchPage searchListings:', {
  //   component: 'SearchPage.duck',
  //   searchParams,
  //   config: config ? 'present' : 'missing',
  //   timestamp: new Date().toISOString()
  // });

  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    return isListingTypeVariant
      ? {
          pub_listingType: listingTypePathParam,
        }
      : config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const omitInvalidCategoryParams = params => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue =
        typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? { [levelKey]: levelValue }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );

    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    // Show out-of-stock products too on grid: do not filter by stock here
    // (Stock handling is visual and at purchase time.)
    return {};
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort: sortParam,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    ...restOfParams
  } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);

  // Relevance is a UI-only key (keyword search). Every other sort is applied
  // server-side on the full filtered set, then paginated.
  const sortMaybe =
    sortParam && sortParam !== config.search.sortConfig.relevanceKey ? { sort: sortParam } : {};

  const params = {
    // The rest of the params except invalid nested category-related params
    // Note: invalid independent search params are still passed through
    ...omitInvalidCategoryParams(restOfParams),
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    perPage,
  };

  // console.log('📡 SearchPage API query params:', {
  //   component: 'SearchPage.duck',
  //   params,
  //   timestamp: new Date().toISOString()
  // });

  // If filtering promotions, fetch ALL products from marketplace first (respecting filters if any)
  if (filterPromotions) {
    // Fetch all products by paginating through all pages
    // Note: params already contains filters from URL if they were specified
    const allListings = [];
    const allIncluded = [];
    const maxPerPage = MAX_API_PER_PAGE; // API limit
    let totalPages = 1;

    // Remove pagination from params for fetching all pages
    const fetchParams = { ...params };
    delete fetchParams.page;
    delete fetchParams.perPage;

    const fetchPage = pageNum => {
      return sdk.listings
        .query({
          ...fetchParams,
          page: pageNum,
          perPage: maxPerPage,
        })
        .then(response => {
          const data = response?.data || {};
          const listings = data?.data || [];
          const included = data?.included || [];

          if (Array.isArray(listings) && listings.length > 0) {
            allListings.push(...listings);
          }
          if (Array.isArray(included) && included.length > 0) {
            allIncluded.push(...included);
          }

          const meta = data?.meta || {};
          totalPages = meta?.totalPages || 1;
          return totalPages;
        });
    };

    // Fetch first page to get total pages
    return fetchPage(1)
      .then(() => {
        // Fetch remaining pages sequentially to avoid overwhelming the API
        let fetchSequence = Promise.resolve();
        for (let page = 2; page <= totalPages; page++) {
          fetchSequence = fetchSequence.then(() => fetchPage(page));
        }
        return fetchSequence;
      })
      .then(() => {
        // Step 1: Filter all listings to get only those with promotions
        const promotionsListings = filterPromotionsFromListings(allListings);

        // Step 2: Sort the full filtered set, then paginate (never sort a single page)
        const currentSort = params.sort || sortParam || config.search.sortConfig.relevanceKey;
        let sortedPromotionsListings = sortListings(promotionsListings, currentSort);

        if (isDefaultSort) {
          const { hasPriority, listings: orderedListings } = applyPriorityOrdering(
            sortedPromotionsListings
          );
          if (hasPriority) {
            sortedPromotionsListings = orderedListings;
          }
        }

        // Step 3: Apply pagination to the filtered and sorted list
        const requestedPage = params.page || 1;
        const requestedPerPage = params.perPage || RESULT_PAGE_SIZE;
        const startIndex = (requestedPage - 1) * requestedPerPage;
        const endIndex = startIndex + requestedPerPage;
        const paginatedListings = sortedPromotionsListings.slice(startIndex, endIndex);

        // Step 4: Combine filtered, sorted and paginated listings into response structure
        const filteredResponse = {
          data: {
            data: paginatedListings,
            meta: {
              totalItems: sortedPromotionsListings.length,
              totalPages: Math.ceil(sortedPromotionsListings.length / requestedPerPage),
              page: requestedPage,
              perPage: requestedPerPage,
            },
            included: allIncluded, // Keep all included entities
          },
        };

        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };

        // Add all listings (including non-promotions) to store for potential use
        const allListingsResponse = {
          data: {
            data: allListings,
            meta: {
              totalItems: allListings.length,
              totalPages: 1,
              page: 1,
              perPage: allListings.length,
            },
            included: allIncluded,
          },
        };
        dispatch(addMarketplaceEntities(allListingsResponse, sanitizeConfig));

        // Dispatch success with filtered, sorted and paginated results
        dispatch(searchListingsSuccess(filteredResponse, currentSort, false, false, isDefaultSort)); // no randomize for promotions
        return filteredResponse;
      })
      .catch(e => {
        const error = storableError(e);
        dispatch(searchListingsError(error));
        if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
          throw e;
        }
      });
  }

  if (isDefaultSort) {
    return sdk.listings
      .query(params)
      .then(response => {
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };
        const listings = response?.data?.data || [];
        const hasPriority = listings.some(l => getSortingPriority(l) != null);

        if (!hasPriority) {
          dispatch(addMarketplaceEntities(response, sanitizeConfig));
          dispatch(
            searchListingsSuccess(
              response,
              sortParam,
              filterPromotions,
              randomizeResults,
              isDefaultSort
            )
          );
          return response;
        }

        // Fetch all products to apply default sort across full result set
        const allListings = [];
        const allIncluded = [];
        const maxPerPage = MAX_API_PER_PAGE; // API limit
        let totalPages = 1;

        // Remove pagination from params for fetching all pages
        const fetchParams = { ...params };
        delete fetchParams.page;
        delete fetchParams.perPage;

        const fetchPage = pageNum => {
          return sdk.listings
            .query({
              ...fetchParams,
              page: pageNum,
              perPage: maxPerPage,
            })
            .then(pageResponse => {
              const data = pageResponse?.data || {};
              const pageListings = data?.data || [];
              const included = data?.included || [];
              if (Array.isArray(pageListings) && pageListings.length > 0) {
                allListings.push(...pageListings);
              }
              if (Array.isArray(included) && included.length > 0) {
                allIncluded.push(...included);
              }
              const meta = data?.meta || {};
              totalPages = meta?.totalPages || 1;
              return totalPages;
            });
        };

        // Fetch first page to get total pages
        return fetchPage(1)
          .then(() => {
            // Fetch remaining pages sequentially to avoid overwhelming the API
            let fetchSequence = Promise.resolve();
            for (let page = 2; page <= totalPages; page++) {
              fetchSequence = fetchSequence.then(() => fetchPage(page));
            }
            return fetchSequence;
          })
          .then(() => {
            let sortedListings = [...allListings];
            const {
              hasPriority: hasPriorityAll,
              listings: orderedListings,
            } = applyPriorityOrdering(sortedListings);
            if (hasPriorityAll) {
              sortedListings = orderedListings;
            }

            const requestedPage = params.page || 1;
            const requestedPerPage = params.perPage || RESULT_PAGE_SIZE;
            const startIndex = (requestedPage - 1) * requestedPerPage;
            const endIndex = startIndex + requestedPerPage;
            const paginatedListings = sortedListings.slice(startIndex, endIndex);

            const filteredResponse = {
              data: {
                data: paginatedListings,
                meta: {
                  totalItems: sortedListings.length,
                  totalPages: Math.ceil(sortedListings.length / requestedPerPage),
                  page: requestedPage,
                  perPage: requestedPerPage,
                },
                included: allIncluded, // Keep all included entities
              },
            };

            const allListingsResponse = {
              data: {
                data: allListings,
                meta: {
                  totalItems: allListings.length,
                  totalPages: 1,
                  page: 1,
                  perPage: allListings.length,
                },
                included: allIncluded,
              },
            };
            dispatch(addMarketplaceEntities(allListingsResponse, sanitizeConfig));
            dispatch(
              searchListingsSuccess(filteredResponse, sortParam, false, false, isDefaultSort)
            );
            return filteredResponse;
          });
      })
      .catch(e => {
        const error = storableError(e);
        dispatch(searchListingsError(error));
        if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
          throw e;
        }
      });
  }

  // Normal query with filters
  return sdk.listings
    .query(params)
    .then(response => {
      // console.log('✅ SearchPage API success:', {
      //   component: 'SearchPage.duck',
      //   resultCount: response.data?.length || 0,
      //   timestamp: new Date().toISOString()
      // });

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(
        searchListingsSuccess(
          response,
          sortParam,
          filterPromotions,
          randomizeResults,
          isDefaultSort
        )
      );
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(searchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

const PER_PAGE_FOR_FILTER_COUNTS = 100;
const filterCountsPromiseCache = {};

export const fetchFilterOptionCounts = (searchParams = {}, filterConfigs = []) => (
  dispatch,
  getState,
  sdk
) => {
  if (!Array.isArray(filterConfigs) || filterConfigs.length === 0) {
    dispatch(fetchFilterCountsSuccess({}));
    return Promise.resolve({});
  }

  if (!sdk || !sdk.listings || typeof sdk.listings.query !== 'function') {
    dispatch(fetchFilterCountsError(new Error('SDK not available for fetching filter counts')));
    return Promise.resolve({});
  }

  // Check if deals filter is active
  const isDealsActive =
    searchParams.deals === 'true' ||
    searchParams.deals === true ||
    searchParams.deals === '1' ||
    searchParams.deals === 1;

  const sanitizedParams = { ...searchParams };
  delete sanitizedParams.page;
  delete sanitizedParams.perPage;
  delete sanitizedParams.include;
  delete sanitizedParams['fields.listing'];
  delete sanitizedParams['fields.user'];
  delete sanitizedParams['fields.image'];
  delete sanitizedParams['limit.images'];
  delete sanitizedParams.mapSearch;
  // Remove deals from API params as it's not a valid API filter
  delete sanitizedParams.deals;

  // Separate category filters from other filters
  const categoryParamNames = ['pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3'];
  const paramsForCategoryCounts = { ...sanitizedParams };
  // Remove category filters for category counts calculation
  categoryParamNames.forEach(paramName => {
    delete paramsForCategoryCounts[paramName];
  });

  const paramsForOtherCounts = { ...sanitizedParams }; // Keep all filters including category

  const baseParamsForCategories = {
    ...paramsForCategoryCounts,
    perPage: PER_PAGE_FOR_FILTER_COUNTS,
  };

  const baseParamsForOthers = {
    ...paramsForOtherCounts,
    perPage: PER_PAGE_FOR_FILTER_COUNTS,
  };

  const filterKeys = filterConfigs
    .map(f => f && f.key)
    .filter(Boolean)
    .sort();
  const cacheKey = JSON.stringify({
    paramsForCategories: baseParamsForCategories,
    paramsForOthers: baseParamsForOthers,
    filters: filterKeys,
    deals: isDealsActive,
  });

  if (filterCountsPromiseCache[cacheKey]) {
    return filterCountsPromiseCache[cacheKey];
  }

  dispatch(fetchFilterCountsRequest());

  // Separate configs for categories and others
  const categoryConfigs = filterConfigs.filter(f => f?.schemaType === 'category');
  const otherConfigs = filterConfigs.filter(f => f?.schemaType !== 'category');

  const allListingsForCategories = [];
  const allListingsForOthers = [];

  const fetchPage = (page, baseParams, allListings) =>
    sdk.listings.query({ ...baseParams, page }).then(response => {
      const data = response?.data || {};
      const listings = data?.data || [];
      if (Array.isArray(listings) && listings.length > 0) {
        allListings.push(...listings);
      }
      const meta = data?.meta || {};
      const totalPages = meta?.totalPages || 1;
      return totalPages;
    });

  // Fetch listings for category counts (without category filter)
  const fetchCategoriesPromise =
    categoryConfigs.length > 0
      ? fetchPage(1, baseParamsForCategories, allListingsForCategories).then(totalPages => {
          let sequence = Promise.resolve();
          for (let page = 2; page <= totalPages; page++) {
            sequence = sequence.then(() =>
              fetchPage(page, baseParamsForCategories, allListingsForCategories)
            );
          }
          return sequence;
        })
      : Promise.resolve();

  // Fetch listings for other counts (with all filters including category)
  const fetchOthersPromise =
    otherConfigs.length > 0
      ? fetchPage(1, baseParamsForOthers, allListingsForOthers).then(totalPages => {
          let sequence = Promise.resolve();
          for (let page = 2; page <= totalPages; page++) {
            sequence = sequence.then(() =>
              fetchPage(page, baseParamsForOthers, allListingsForOthers)
            );
          }
          return sequence;
        })
      : Promise.resolve();

  const promise = Promise.all([fetchCategoriesPromise, fetchOthersPromise])
    .then(() => {
      // Filter listings for promotions if deals filter is active
      const filteredListingsForCategories = isDealsActive
        ? allListingsForCategories.filter(hasActivePromotion)
        : allListingsForCategories;
      const filteredListingsForOthers = isDealsActive
        ? allListingsForOthers.filter(hasActivePromotion)
        : allListingsForOthers;

      // Calculate counts separately
      const categoryCounts =
        categoryConfigs.length > 0
          ? getFilterOptionCounts(filteredListingsForCategories, categoryConfigs)
          : {};
      const otherCounts =
        otherConfigs.length > 0
          ? getFilterOptionCounts(filteredListingsForOthers, otherConfigs)
          : {};

      // Merge counts
      const counts = { ...categoryCounts, ...otherCounts };
      dispatch(fetchFilterCountsSuccess(counts));
      return counts;
    })
    .catch(e => {
      dispatch(fetchFilterCountsError(storableError(e)));
      return {};
    })
    .finally(() => {
      delete filterCountsPromiseCache[cacheKey];
    });

  filterCountsPromiseCache[cacheKey] = promise;
  return promise;
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const ensureSlugEntries =
    typeof window !== 'undefined' ? loadSlugEntries(config?.marketplaceRootURL) : Promise.resolve();

  return ensureSlugEntries.then(() => {
    // In private marketplace mode, this page won't fetch data if the user is unauthorized
    const slugSearchParams = getSlugSearchParams(params, config);
    const listingTypePathParam = slugSearchParams.pub_listingType;

    const state = getState();
    const perPageOverride = state.SearchPage?.perPageOverride;
    const currentUser = state.user?.currentUser;
    const isAuthorized = currentUser && isUserAuthorized(currentUser);
    const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
    const isPrivateMarketplace = config.accessControl.marketplace.private === true;
    const canFetchData =
      !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
    if (!canFetchData) {
      return Promise.resolve();
    }

    const queryParams = parse(search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const mergedQueryParams = { ...queryParams, ...slugSearchParams };

    // Check if deals parameter is in query string
    const { page = 1, perPage: perPageParam, address, origin, deals, ...rest } = mergedQueryParams;
    const isDealsPage = deals === 'true' || deals === true || deals === '1' || deals === 1;

    // Normalize public params (brand/category/subcategory/etc.) into internal pub_* keys
    const filterConfigs = {
      listingFieldsConfig: config?.listing?.listingFields || [],
      defaultFiltersConfig: config?.search?.defaultFilters || [],
    };

    // Normalize all filters (including when deals is active)
    const normalizedRest = normalizeIncomingSearchParams(rest, filterConfigs);
    const { shouldRandomizeResults, shouldApplyPrioritySort } = resolveSearchFlags({
      normalizedRest,
      listingTypePathParam,
      isDealsPage,
      hasSlugPath: !!params?.slug,
    });
    const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

    const listingTypeVariantMaybe = listingTypePathParam
      ? { listingTypePathParam, isListingTypeVariant: true }
      : {};

    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    // Use perPage override when set, otherwise prefer URL param or default RESULT_PAGE_SIZE
    const perPageFromQuery = perPageParam ? parseInt(perPageParam, 10) : RESULT_PAGE_SIZE;
    const perPage = Number.isFinite(perPageOverride) ? perPageOverride : perPageFromQuery;

    const searchListingsCall = searchListings(
      {
        ...normalizedRest,
        ...originMaybe,
        ...listingTypeVariantMaybe,
        page,
        perPage: perPage || RESULT_PAGE_SIZE,
        include: ['author', 'images'],
        'fields.listing': [
          'title',
          'geolocation',
          'price',
          'deleted',
          'state',
          'createdAt',
          'metadata',
          'publicData.listingType',
          'publicData.transactionProcessAlias',
          'publicData.unitType',
          'publicData.cardStyle',
          'publicData.brand',
          'publicData.original_price',
          'publicData.sales',
          // Added for product grid meta display
          'publicData.categoryLevel1',
          'publicData.deviceType',
          'publicData.connectivity',
          // These help rendering of 'purchase' listings,
          // when transitioning from search page to listing page
          'publicData.pickupEnabled',
          'publicData.shippingEnabled',
          'publicData.priceVariationsEnabled',
          'publicData.priceVariants',
          'publicData.sorting_priority',
        ],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        'fields.image': [
          'variants.scaled-small',
          'variants.scaled-medium',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      },
      config,
      isDealsPage, // Pass deals flag to searchListings
      shouldRandomizeResults,
      shouldApplyPrioritySort
    );

    return dispatch(searchListingsCall);
  });
};
